import { Box, Divider, type TabData, Text } from '@ltvco/refresh-lib/theme';
import {
  type SearchData,
  type supportingTypes,
  type serializedTypes,
  EmptyNeighborhoodReportPage,
  type RecentReportSerialized,
  type TopHapProperty,
} from '@ltvco/refresh-lib/v1';

type generateTabsProps = {
  tabsReports: RecentReportSerialized[];
  reportDetails: TopHapProperty;
  address: string;
  sexOffenders: supportingTypes.SexOffenders;
  propertyReportGeoLat?: number;
  propertyReportGeoLong?: number;
  //Commenting out the allWidgetsData as tophap widgets are not working
  // allWidgetsData: WidgetsWithSectionTitleProps[];
  // topHapInstance: Tophap;
};

export type ExtendedTabData = TabData & {
  permalink: string;
};

export const getReportDetails = (
  property: serializedTypes.SerializedProperty
): TopHapProperty => {
  const reportDetails = {
    fullAddress: property?.parcel_address?.full || '',
    city: property?.parcel_address?.parts?.city || '',
    house_number: property?.parcel_address?.parts?.house_number || '',
    state: property?.parcel_address?.parts?.state || '',
    streetName: property?.parcel_address?.parts?.streetName || '',
    streetType: property?.parcel_address?.parts?.streetType || '',
    title: property?.parcel_address?.parts?.street || '',
    subtitle: `${property?.parcel_address?.parts?.cityState || ''} ${
      property?.parcel_address?.parts?.zip || ''
    }`,
    zip: property?.parcel_address?.parts?.zip || '',
    content: property?.county ? `${property?.county} County` : null,
  } as TopHapProperty;

  return reportDetails;
};

export function generateTabs({
  tabsReports,
  reportDetails,
}: //Commenting out the allWidgetsData as tophap widgets are not working
// allWidgetsData,
// topHapInstance,
generateTabsProps): ExtendedTabData[] {
  return tabsReports?.map((report) => {
    const { detailsCityState, reportData } = report;
    return {
      label: `${reportData || ''}, ${detailsCityState || ''}`,
      content: report.id ? (
        <Box>
          <Divider />
          <Box marginTop={2} marginBottom={2}>
            <Text variant="h2" color="primary" fontWeight="bold">
              {reportDetails?.title}
            </Text>
            <Text variant="h4">{reportDetails?.subtitle}</Text>
          </Box>
          {/* {allWidgetsData.map((el) => {
            return (
              <TopHapSection
                widgetData={el.widgetData}
                property={reportDetails}
                permalink={report.id}
                sectionTitle={el.sectionTitle}
                key={el.sectionTitle}
                topHapInstance={topHapInstance}
              />
            );
          })} */}
        </Box>
      ) : (
        <EmptyNeighborhoodReportPage />
      ),
      permalink: report.id,
    };
  }) as ExtendedTabData[];
}

export function generateFullAddressFromParams(
  queryParams: URLSearchParams
): string {
  const address = queryParams.get('address') || '';
  const city = queryParams.get('city') || '';
  const state = queryParams.get('state') || '';
  const zipcode = queryParams.get('zipcode') || '';

  return `${address} ${city}, ${state} ${zipcode}`;
}

// There are race condition and sorting issues where searching a new report and getting the recent reports list updated
export function optimisticRenderingForNewReports(
  reportsList: RecentReportSerialized[],
  data: SearchData,
  reportsToDisplay: number
) {
  const permalink = data.permalink;
  const queryParams = new URLSearchParams(data.searchParams);
  const address = queryParams.get('address');
  const city = queryParams.get('city');
  const state = queryParams.get('state');

  const existingReport = reportsList.some((report) => report.id === permalink);
  if (existingReport) {
    return;
  }

  const optimisticReport = {
    id: permalink,
    reportData: address,
    detailsCityState: `${city}, ${state}`,
  } as RecentReportSerialized;

  // Remove last report if the list is full
  if (reportsList.length > reportsToDisplay - 1) {
    reportsList.pop();
  }

  // Add optimistic report at the front of the list
  reportsList.unshift(optimisticReport);
  return reportsList;
}

// Recent Searches Utility Functions
const RECENT_SEARCHES_KEY = 'neighborhood_recent_searches';
const MAX_RECENT_SEARCHES = 3;

export interface RecentSearch {
  address: string;
  timestamp: number;
}

/**
 * Get recent searches from localStorage
 * Returns up to 3 most recent searches, ordered by most recent first
 */
export const getRecentSearches = (): RecentSearch[] => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!stored) {
      return [];
    }

    const searches: RecentSearch[] = JSON.parse(stored);
    return searches
      .sort((a, b) => b.timestamp - a.timestamp) // Most recent first
      .slice(0, MAX_RECENT_SEARCHES);
  } catch (error) {
    console.warn('Error reading recent searches from localStorage:', error);
    return [];
  }
};

/**
 * Add a new search to recent searches
 * Automatically removes duplicates and maintains max limit
 */
export const addRecentSearch = (address: string): void => {
  if (!address?.trim()) {
    return;
  }

  try {
    const currentSearches = getRecentSearches();
    const normalizedAddress = address.trim();

    // Remove existing entry if it exists (to avoid duplicates)
    const filteredSearches = currentSearches.filter(
      (search) =>
        search.address.toLowerCase() !== normalizedAddress.toLowerCase()
    );

    // Add new search at the beginning
    const newSearches: RecentSearch[] = [
      { address: normalizedAddress, timestamp: Date.now() },
      ...filteredSearches,
    ].slice(0, MAX_RECENT_SEARCHES);

    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(newSearches));
  } catch (error) {
    console.warn('Error saving recent search to localStorage:', error);
  }
};

/**
 * Clear all recent searches
 */
export const clearRecentSearches = (): void => {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch (error) {
    console.warn('Error clearing recent searches from localStorage:', error);
  }
};
